import { useCallback, useEffect, useState } from 'react'
import { Container, Row, Col, Spinner, Alert, Button, Badge, Card, Breadcrumb, Form } from 'react-bootstrap'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AnimationOnScroll } from 'react-animation-on-scroll'
import { Helmet } from 'react-helmet-async'
import { FaLock } from 'react-icons/fa'
import http from '../../helpers/Request'
import EventRegistrationForm from '../../components/EventRegistrationForm'
import moment from 'moment'
import { formatDateTime } from '../../helpers/BlogText'

const EventDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showRegistrationForm, setShowRegistrationForm] = useState(false)
  const [privateAccessRequired, setPrivateAccessRequired] = useState(false)
  const [privateEventPreview, setPrivateEventPreview] = useState(null)
  const [privatePassword, setPrivatePassword] = useState('')
  const [privatePasswordError, setPrivatePasswordError] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [unlocking, setUnlocking] = useState(false)

  const accessStorageKey = `siberwander_event_access_${slug}`

  const fetchEvent = useCallback(async (token = '') => {
    try {
      setLoading(true)
      setError('')
      const config = token ? { params: { event_access_token: token } } : {}
      const response = await http.get(`/api/events/${slug}`, config)
      
      if (response.data.status) {
        setEvent(response.data.data)
        setPrivateAccessRequired(false)
        setPrivateEventPreview(null)
        setAccessToken(token)
      } else {
        setError('Event not found')
      }
    } catch (err) {
      console.error('Error fetching event:', err)
      if (err.response?.status === 403 && err.response?.data?.private_required) {
        if (token) {
          sessionStorage.removeItem(accessStorageKey)
        }
        setEvent(null)
        setPrivateAccessRequired(true)
        setPrivateEventPreview(err.response?.data?.event_preview || null)
        setPrivatePasswordError('')
      } else if (err.response?.status === 404) {
        setError('Event not found')
      } else {
        setError('Failed to load event details')
      }
    } finally {
      setLoading(false)
    }
  }, [slug, accessStorageKey])

  const handlePrivateAccessSubmit = async (e) => {
    e.preventDefault()

    if (!privatePassword.trim()) {
      setPrivatePasswordError('Please enter the event password.')
      return
    }

    try {
      setUnlocking(true)
      setPrivatePasswordError('')

      const response = await http.post(`/api/events/${slug}/access`, {
        password: privatePassword.trim(),
      })

      if (response.data.status) {
        const token = response.data.event_access_token || ''
        setEvent(response.data.data)
        setAccessToken(token)
        setPrivateAccessRequired(false)
        setPrivatePassword('')

        if (token) {
          sessionStorage.setItem(accessStorageKey, token)
        }
      } else {
        setPrivatePasswordError(response.data.error || 'Invalid event password.')
      }
    } catch (err) {
      if (err.response?.data?.event_preview) {
        setPrivateEventPreview(err.response.data.event_preview)
      }
      setPrivatePasswordError(err.response?.data?.error || 'Invalid event password.')
    } finally {
      setUnlocking(false)
    }
  }

  const getCountdownText = (deadline) => {
    const now = moment()
    const deadlineDate = moment(deadline)
    const duration = moment.duration(deadlineDate.diff(now))
    
    if (duration.asMilliseconds() <= 0) {
      return 'Registration Closed'
    }
    
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    
    if (days > 0) {
      return `${days} days, ${hours} hours left to register`
    } else if (hours > 0) {
      return `${hours} hours, ${minutes} minutes left to register`
    } else {
      return `${minutes} minutes left to register`
    }
  }

  const isRegistrationClosed = (deadline) => {
    return moment().isAfter(moment(deadline))
  }

  const getEventTypeBadge = (type) => {
    return type === 'online' ? (
      <Badge bg="primary" className="me-2">
        <i className="fas fa-video me-1"></i> Online Event
      </Badge>
    ) : (
      <Badge bg="success" className="me-2">
        <i className="fas fa-map-marker-alt me-1"></i> Offline Event
      </Badge>
    )
  }

  const getAvailabilityText = (event) => {
    if (event.max_participants === null) {
      return 'Unlimited slots available'
    }
    
    const available = event.available_slots
    if (available <= 0) {
      return 'Fully Booked'
    }
    
    return `${available} of ${event.max_participants} slots available`
  }

  const isFullyBooked = (event) => {
    return event.max_participants !== null && event.available_slots <= 0
  }

  const handleRegisterClick = () => {
    setShowRegistrationForm(true)
  }

  const handleRegistrationSuccess = () => {
    setShowRegistrationForm(false)
    // Refresh event data to update available slots
    fetchEvent(accessToken)
  }

  const handleRegistrationCancel = () => {
    setShowRegistrationForm(false)
  }

  useEffect(() => {
    if (slug) {
      const storedAccessToken = sessionStorage.getItem(accessStorageKey) || ''
      fetchEvent(storedAccessToken)
    }
  }, [slug, accessStorageKey, fetchEvent])

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading event details...</p>
        </div>
      </Container>
    )
  }

  if (privateAccessRequired) {
    const previewTitle = privateEventPreview?.title || 'Private Event'

    return (
      <>
        <Helmet>
          <title>{previewTitle} - SiberWander Events</title>
          <meta name="description" content="This SiberWander event requires a password." />
        </Helmet>

        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={9} md={10}>
              <Card className="card-shadow event-password-card">
                <Row className="g-0 event-password-layout">
                  <Col lg={6}>
                    <div className="event-password-preview">
                      {privateEventPreview?.image ? (
                        <img
                          src={privateEventPreview.image}
                          alt={previewTitle}
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      ) : (
                        <div className="event-password-preview-fallback">
                          <FaLock />
                        </div>
                      )}
                      <div className="event-password-preview-content">
                        {privateEventPreview?.event_type && (
                          <div className="mb-2">
                            {getEventTypeBadge(privateEventPreview.event_type)}
                          </div>
                        )}
                        <h1 className="h3 mb-0">{previewTitle}</h1>
                      </div>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <Card.Body className="p-4 p-lg-5">
                      <div className="text-center mb-4">
                        <div className="event-password-icon mb-3">
                          <FaLock />
                        </div>
                      </div>

                      <div className="mb-4">
                        <h2 className="h4 mb-2 text-center">Private Event</h2>
                        <p className="text-muted mb-0 text-center">
                          Enter the event password to view details and register.
                        </p>
                      </div>

                      {privatePasswordError && (
                        <Alert variant="danger">{privatePasswordError}</Alert>
                      )}

                      <Form onSubmit={handlePrivateAccessSubmit}>
                        <Form.Group className="mb-3" controlId="eventPassword">
                          <Form.Control
                            type="password"
                            className="password-no-reveal"
                            value={privatePassword}
                            onChange={(e) => setPrivatePassword(e.target.value)}
                            placeholder="Enter password"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            disabled={unlocking}
                          />
                        </Form.Group>

                        <div className="d-grid gap-2">
                          <Button variant="brand" type="submit" disabled={unlocking}>
                            {unlocking ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Checking...
                              </>
                            ) : (
                              'Open Event'
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            type="button"
                            onClick={() => navigate('/events')}
                            disabled={unlocking}
                          >
                            Back to Events
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  if (error) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Alert variant="danger">
            {error}
          </Alert>
          <Button variant="outline-primary" onClick={() => navigate('/events')}>
            Back to Events
          </Button>
        </div>
      </Container>
    )
  }

  if (!event) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <h4>Event not found</h4>
          <p className="text-muted">The event you're looking for doesn't exist or has been removed.</p>
          <Button variant="outline-primary" onClick={() => navigate('/events')}>
            Back to Events
          </Button>
        </div>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>{event.title} - SiberWander Events</title>
        <meta name="description" content={event.description ? event.description.replace(/<[^>]*>/g, '').substring(0, 160) : `Join ${event.title} - an exclusive cybersecurity event by SiberWander.`} />
        <meta name="keywords" content={`${event.title}, cybersecurity event, SiberWander, ${event.event_type} event`} />
      </Helmet>

      <div className='wrap-hidden'>
        {/* Event Details */}
        <Container className='py-5'>
          <Row className='mb-4'>
            <Col xs={12}>
              <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
                  Home
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/events" }}>
                  Events
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{event.title}</Breadcrumb.Item>
              </Breadcrumb>
            </Col>
          </Row>
          <Row className='g-4'>
            {/* Event Content */}
            <Col lg={8} className="mb-5">
              <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8}>
                {/* Event Image */}
                {event.image && (
                  <div className="mb-4">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="img-fluid rounded"
                      style={{ width: '100%', height: 'auto'}}
                      onError={(e) => {e.target.style.display = 'none'}}
                    />
                  </div>
                )}

                {/* Event Header */}
                <div className="mb-4">
                  <div className="mb-3">
                    {getEventTypeBadge(event.event_type)}
                    <Badge 
                      bg={isRegistrationClosed(event.registration_deadline) ? 'danger' : 'warning'} 
                      text="dark"
                    >
                      <i className="fas fa-clock me-1"></i>
                      {getCountdownText(event.registration_deadline)}
                    </Badge>
                  </div>
                  
                  <h1 className="h2 mb-3 fw-bold">{event.title}</h1>
                  
                  {/* Event Meta Information */}
                  <div className="event-meta mb-4">
                    <Row>
                      <Col md={6} className="mb-2">
                        <i className="fas fa-calendar-alt text-brand"></i>
                        <strong>Event Date:</strong><br/>
                        <span>{formatDateTime(event.event_date)}</span>
                      </Col>
                      <Col md={6} className="mb-2">
                        <i className="fas fa-clock text-brand"></i>
                        <strong>Registration Deadline:</strong><br/>
                        <span>{formatDateTime(event.registration_deadline)}</span>
                      </Col>
                      
                      {event.event_type === 'offline' && event.event_location && (
                        <Col md={6} className="mb-2">
                          <i className="fas fa-map-marker-alt text-brand"></i>
                          <strong>Location:</strong><br/>
                          <span>{event.event_location}</span>
                        </Col>
                      )}
                      
                      <Col md={6} className="mb-2">
                        <i className="fas fa-users text-brand"></i>
                        <strong>Availability:</strong><br/>
                        <span>{getAvailabilityText(event)}</span>
                      </Col>
                    </Row>
                  </div>
                </div>

                {/* Event Description */}
                {event.description && (
                  <div className="event-description">
                    <h5 className="mb-3">About This Event</h5>
                    <div 
                      className="content-output"
                      dangerouslySetInnerHTML={{ __html: event.description }}
                    />
                  </div>
                )}
              </AnimationOnScroll>
            </Col>

            {/* Registration Sidebar */}
            <Col lg={4}>
              <AnimationOnScroll animateOnce={true} animateIn="animate__slideInRight" duration={.8}>
                <Card className="card-shadow">
                  <Card.Body>
                    {showRegistrationForm ? (
                      <EventRegistrationForm 
                        event={event}
                        onSuccess={handleRegistrationSuccess}
                        onCancel={handleRegistrationCancel}
                        accessToken={accessToken}
                      />
                    ) : (
                      <>
                        <h5 className="mb-3">Register for This Event</h5>
                        
                        {/* Quick Event Info */}
                        <div className="mb-3 p-3 bg-light rounded">
                          <div className="mb-2 d-flex flex-row gap-3">
                            <strong>📅</strong><strong>{formatDateTime(event.event_date)}</strong>
                          </div>
                          
                          {event.event_type === 'offline' && event.event_location && (
                            <div className="mb-2 d-flex flex-row gap-3">
                              <strong>📍</strong><strong>{event.event_location}</strong>
                            </div>
                          )}
                          
                          {event.event_type === 'online' && (
                            <div className="mb-2 d-flex flex-row gap-3">
                              <strong>💻</strong><strong>Online Event</strong>
                            </div>
                          )}
                          
                          <div className="mb-2 d-flex flex-row gap-3">
                            <strong>👥</strong><strong>{getAvailabilityText(event)}</strong>
                          </div>
                          
                          <div className="d-flex flex-row gap-3">
                            <strong>⏰</strong><strong>{getCountdownText(event.registration_deadline)}</strong>
                          </div>
                        </div>

                        {/* Registration Button */}
                        <div className="d-grid">
                          <Button 
                            variant="brand"
                            onClick={handleRegisterClick}
                            disabled={isRegistrationClosed(event.registration_deadline) || isFullyBooked(event)}
                          >
                            {isRegistrationClosed(event.registration_deadline) 
                              ? 'Registration Closed' 
                              : isFullyBooked(event)
                              ? 'Fully Booked'
                              : 'Register Now'
                            }
                          </Button>
                        </div>

                        {/* Additional Info */}
                        <div className="mt-3 text-center">
                          <small className="text-muted">
                            Registration is free and includes email confirmation
                          </small>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </AnimationOnScroll>
            </Col>
          </Row>
        </Container>

        {/* Related/Other Events Section */}
        <div className='bg-light py-5'>
          <Container>
            <Row>
              <Col xs={12}>
                <AnimationOnScroll animateOnce={true} animateIn="animate__fadeIn" duration={.8} animatePreScroll={true} offset={100}>
                  <div className='text-center mb-4'>
                    <h3>Other Upcoming Events</h3>
                    <p className='text-muted'>Discover more cybersecurity events and workshops</p>
                  </div>
                  
                  <div className="text-center">
                    <Button variant="outline-primary" onClick={() => navigate('/events')}>
                      View All Events
                    </Button>
                  </div>
                </AnimationOnScroll>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </>
  )
}

export default EventDetail
